import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Divider from '@mui/joy/Divider';
import Switch from '@mui/joy/Switch';
import Alert from '@mui/joy/Alert';
import Grid from '@mui/joy/Grid';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GeneralApiService from "../../axios/Axios";
import { GetAllFranchises } from "../../axios/Axios";
import { toast } from 'react-toastify';

interface SampleLead {
    id: number;
    customer_name: string;
    customer_phone: string;
    services: string;
    total_price: number;
    created_at: string;
}

interface WhatsAppTemplate {
    sid: string;
    friendly_name?: string;
    language?: string;
    body?: string;
    variables?: string[];
}

interface FranchiseOption {
    id: number;
    location: string;
}

interface PreviewResponse {
    count: number;
    skipped_already_sent: number;
    sample_leads: SampleLead[];
}

interface Campaign {
    id: number;
    created_at: string;
    template_sid: string;
    template_name: string;
    template_body_preview: string;
    recipients_count: number;
    sent_count: number;
    failed_count: number;
    skipped_count: number;
    status: 'pending' | 'sending' | 'completed' | 'failed';
}

const VARIABLE_FIELDS = [
    { value: 'first_name', label: 'First Name' },
    { value: 'customer_name', label: 'Full Name' },
    { value: 'services', label: 'Services' },
    { value: 'total_price', label: 'Total Price' },
    { value: 'postcode', label: 'Postcode' },
    { value: 'franchise_phone', label: 'Franchise Phone' },
    { value: 'franchise_owner', label: 'Franchise Owner' },
];

const StatusChip = ({ status }: { status: Campaign['status'] }) => {
    const config = {
        pending: { color: 'warning' as const, icon: <WarningIcon sx={{ fontSize: 14 }} /> },
        sending: { color: 'primary' as const, icon: <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> },
        completed: { color: 'success' as const, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        failed: { color: 'danger' as const, icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    };
    const { color, icon } = config[status] || config.pending;

    return (
        <Chip variant="soft" color={color} size="sm" startDecorator={icon}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </Chip>
    );
};

export default function BulkWhatsApp() {
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
    const [selectedTemplateSid, setSelectedTemplateSid] = useState('');
    const [variableMapping, setVariableMapping] = useState<Record<string, string>>({});
    const [loadingTemplates, setLoadingTemplates] = useState(true);

    const [lastXLeads, setLastXLeads] = useState(100);
    const [cooldownDays, setCooldownDays] = useState(0);
    const [newCustomersOnly, setNewCustomersOnly] = useState(false);
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');

    const [franchises, setFranchises] = useState<FranchiseOption[]>([]);
    const [loadingFranchises, setLoadingFranchises] = useState(true);

    const [previewCount, setPreviewCount] = useState<number | null>(null);
    const [skippedAlreadySent, setSkippedAlreadySent] = useState(0);
    const [sampleLeads, setSampleLeads] = useState<SampleLead[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);

    const [sending, setSending] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [testLeadId, setTestLeadId] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    const selectedTemplate = useMemo(
        () => templates.find((template) => template.sid === selectedTemplateSid),
        [templates, selectedTemplateSid]
    );
    const templateVariables = selectedTemplate?.variables || [];
    const allVariablesMapped = templateVariables.every((variable) => Boolean(variableMapping[variable]));

    const loadTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        try {
            const data = await GeneralApiService.getBulkWhatsAppTemplates();
            const results = data.results || [];
            setTemplates(results);
            setSelectedTemplateSid((current) => current || results[0]?.sid || '');
        } catch (error: any) {
            console.error('Failed to load WhatsApp templates:', error);
            toast.error(error.response?.data?.error || 'Failed to load WhatsApp templates');
        } finally {
            setLoadingTemplates(false);
        }
    }, []);

    const loadCampaigns = useCallback(async () => {
        setLoadingCampaigns(true);
        try {
            const data = await GeneralApiService.getBulkWhatsAppCampaigns();
            setCampaigns(data.results || []);
        } catch (error) {
            console.error('Failed to load WhatsApp campaigns:', error);
            toast.error('Failed to load WhatsApp campaign history');
        } finally {
            setLoadingCampaigns(false);
        }
    }, []);

    const loadFranchises = useCallback(async () => {
        setLoadingFranchises(true);
        try {
            const response = await GetAllFranchises();
            setFranchises(response.data || []);
        } catch (error) {
            console.error('Failed to load franchises:', error);
            toast.error('Failed to load franchises');
        } finally {
            setLoadingFranchises(false);
        }
    }, []);

    const fetchPreview = useCallback(async () => {
        if (!selectedTemplateSid) {
            setPreviewCount(null);
            setSampleLeads([]);
            setSkippedAlreadySent(0);
            return;
        }

        setLoadingPreview(true);
        try {
            const data: PreviewResponse = await GeneralApiService.getBulkWhatsAppPreview(
                selectedTemplateSid,
                lastXLeads,
                cooldownDays,
                newCustomersOnly,
                selectedFranchiseId || null
            );
            setPreviewCount(data.count);
            setSkippedAlreadySent(data.skipped_already_sent || 0);
            setSampleLeads(data.sample_leads || []);
        } catch (error) {
            console.error('Failed to fetch WhatsApp preview:', error);
            toast.error('Failed to load WhatsApp recipient preview');
            setPreviewCount(null);
            setSkippedAlreadySent(0);
            setSampleLeads([]);
        } finally {
            setLoadingPreview(false);
        }
    }, [selectedTemplateSid, lastXLeads, cooldownDays, newCustomersOnly, selectedFranchiseId]);

    useEffect(() => {
        loadTemplates();
        loadCampaigns();
        loadFranchises();
    }, [loadTemplates, loadCampaigns, loadFranchises]);

    useEffect(() => {
        const variables = selectedTemplate?.variables || [];
        setVariableMapping((current) => {
            const nextMapping: Record<string, string> = {};
            variables.forEach((variable, index) => {
                nextMapping[variable] = current[variable] || VARIABLE_FIELDS[index]?.value || '';
            });
            return nextMapping;
        });
    }, [selectedTemplate]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchPreview();
        }, 500);
        return () => clearTimeout(debounceTimer);
    }, [fetchPreview]);

    const handleMappingChange = (variable: string, value: string | null) => {
        setVariableMapping((current) => ({
            ...current,
            [variable]: value || '',
        }));
    };

    const handleSend = () => {
        if (!selectedTemplate) {
            toast.error('Please select a WhatsApp template');
            return;
        }
        if (!allVariablesMapped) {
            toast.error('Please map all template variables');
            return;
        }
        if (previewCount === 0) {
            toast.error('No recipients match your criteria');
            return;
        }
        setConfirmModalOpen(true);
    };

    const confirmSend = async () => {
        if (!selectedTemplate) return;

        setConfirmModalOpen(false);
        setSending(true);
        try {
            const response = await GeneralApiService.sendBulkWhatsApp(
                selectedTemplate.sid,
                selectedTemplate.friendly_name || '',
                selectedTemplate.body || '',
                variableMapping,
                lastXLeads,
                cooldownDays,
                newCustomersOnly,
                selectedFranchiseId || null
            );
            toast.success(`Campaign started! Sending to ${response.recipients_count} recipients.`);
            loadCampaigns();
            fetchPreview();
        } catch (error: any) {
            console.error('Failed to send bulk WhatsApp:', error);
            toast.error(error.response?.data?.error || 'Failed to send WhatsApp campaign');
        } finally {
            setSending(false);
        }
    };

    const handleSendTest = async () => {
        if (!selectedTemplate) {
            toast.error('Please select a WhatsApp template');
            return;
        }
        if (!testLeadId.trim()) {
            toast.error('Please enter a Lead ID');
            return;
        }
        if (!allVariablesMapped) {
            toast.error('Please map all template variables');
            return;
        }

        setSendingTest(true);
        try {
            const response = await GeneralApiService.sendTestBulkWhatsApp(
                selectedTemplate.sid,
                selectedTemplate.friendly_name || '',
                selectedTemplate.body || '',
                variableMapping,
                parseInt(testLeadId)
            );
            toast.success(`Test WhatsApp sent to ${response.customer_name} (${response.phone})`);
            fetchPreview();
        } catch (error: any) {
            console.error('Failed to send test WhatsApp:', error);
            toast.error(error.response?.data?.error || 'Failed to send test WhatsApp');
        } finally {
            setSendingTest(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid xs={12} md={4}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography level="title-md" sx={{ mb: 2 }}>
                                Target Filters
                            </Typography>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Franchise</FormLabel>
                                <Select
                                    value={selectedFranchiseId}
                                    onChange={(_, value) => setSelectedFranchiseId(value || '')}
                                    disabled={loadingFranchises}
                                >
                                    <Option value="">All Franchises</Option>
                                    {franchises.map((franchise) => (
                                        <Option key={franchise.id} value={String(franchise.id)}>
                                            {franchise.location}
                                        </Option>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    Limit this campaign to customers assigned to one franchise.
                                </FormHelperText>
                            </FormControl>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Last X Leads</FormLabel>
                                <Input
                                    type="number"
                                    value={lastXLeads}
                                    onChange={(e) => setLastXLeads(Math.max(1, parseInt(e.target.value) || 1))}
                                    slotProps={{ input: { min: 1, max: 10000 } }}
                                />
                                <FormHelperText>Target the most recent leads, ordered by date.</FormHelperText>
                            </FormControl>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>SMS Cooldown Filter (days)</FormLabel>
                                <Input
                                    type="number"
                                    value={cooldownDays}
                                    onChange={(e) => setCooldownDays(Math.max(0, parseInt(e.target.value) || 0))}
                                    slotProps={{ input: { min: 0, max: 365 } }}
                                />
                                <FormHelperText>
                                    Optional: reuse Bulk SMS lead cooldown filter. Duplicate WhatsApp templates are always blocked forever.
                                </FormHelperText>
                            </FormControl>

                            <Divider sx={{ my: 2 }} />

                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <FormLabel>New Customers Only</FormLabel>
                                    <FormHelperText sx={{ mt: 0 }}>
                                        Only message customers created after 23rd Feb 2026
                                    </FormHelperText>
                                </Box>
                                <Switch
                                    checked={newCustomersOnly}
                                    onChange={(e) => setNewCustomersOnly(e.target.checked)}
                                    color={newCustomersOnly ? 'success' : 'neutral'}
                                />
                            </FormControl>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography level="title-md">Preview</Typography>
                                <IconButton size="sm" variant="plain" onClick={fetchPreview} disabled={loadingPreview || !selectedTemplateSid}>
                                    <RefreshIcon />
                                </IconButton>
                            </Box>

                            {loadingPreview ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size="md" />
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ textAlign: 'center', py: 2, px: 3, bgcolor: 'background.level1', borderRadius: 'md', mb: 2 }}>
                                        <Typography level="h2" sx={{ color: 'success.500' }}>
                                            {previewCount?.toLocaleString() || 0}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                            recipients will receive this WhatsApp template
                                        </Typography>
                                        {skippedAlreadySent > 0 && (
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                                {skippedAlreadySent.toLocaleString()} skipped because they already received this template
                                            </Typography>
                                        )}
                                    </Box>

                                    {sampleLeads.length > 0 && (
                                        <>
                                            <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                                Sample Recipients:
                                            </Typography>
                                            <Box sx={{ fontSize: 'xs' }}>
                                                {sampleLeads.map((lead, index) => (
                                                    <Box
                                                        key={lead.id}
                                                        sx={{
                                                            py: 0.75,
                                                            borderBottom: index < sampleLeads.length - 1 ? '1px solid' : 'none',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <Typography level="body-xs" sx={{ fontWeight: 'md' }}>
                                                            {lead.customer_name} ({lead.customer_phone})
                                                        </Typography>
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                            {lead.services} - £{lead.total_price}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                    {previewCount === 0 && (
                                        <Alert color="warning" size="sm">
                                            No leads match your current filters, or all eligible customers already received this template.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid xs={12} md={8}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography level="title-md" sx={{ mb: 2 }}>
                                Template Builder Campaign
                            </Typography>

                            <Alert color="neutral" size="sm" sx={{ mb: 2 }} startDecorator={<InfoOutlinedIcon />}>
                                Templates are pulled from Twilio Content Template Builder. Each customer can receive each template SID only once.
                            </Alert>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Twilio WhatsApp Template</FormLabel>
                                <Select
                                    value={selectedTemplateSid || null}
                                    onChange={(_, value) => setSelectedTemplateSid(value || '')}
                                    disabled={loadingTemplates}
                                    startDecorator={loadingTemplates ? <CircularProgress size="sm" /> : <WhatsAppIcon />}
                                >
                                    {templates.map((template) => (
                                        <Option key={template.sid} value={template.sid}>
                                            {template.friendly_name || template.sid} ({template.sid})
                                        </Option>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    <Button size="sm" variant="plain" onClick={loadTemplates} disabled={loadingTemplates} sx={{ px: 0 }}>
                                        Refresh templates
                                    </Button>
                                </FormHelperText>
                            </FormControl>

                            {selectedTemplate && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                                    <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                        Template Preview
                                    </Typography>
                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                        {selectedTemplate.body || 'No body preview available from Twilio.'}
                                    </Typography>
                                </Box>
                            )}

                            <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                Variable Mapping
                            </Typography>
                            {templateVariables.length === 0 ? (
                                <Alert color="neutral" size="sm" sx={{ mb: 2 }}>
                                    This template has no variables, or Twilio did not return variable metadata.
                                </Alert>
                            ) : (
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    {templateVariables.map((variable) => (
                                        <Grid xs={12} sm={6} key={variable}>
                                            <FormControl>
                                                <FormLabel>{`{{${variable}}}`}</FormLabel>
                                                <Select
                                                    value={variableMapping[variable] || null}
                                                    onChange={(_, value) => handleMappingChange(variable, value)}
                                                >
                                                    {VARIABLE_FIELDS.map((field) => (
                                                        <Option key={field.value} value={field.value}>
                                                            {field.label}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                                <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                    Send Test Template
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                    <FormControl sx={{ flex: 1 }}>
                                        <Input
                                            size="sm"
                                            placeholder="Enter Lead ID"
                                            value={testLeadId}
                                            onChange={(e) => setTestLeadId(e.target.value)}
                                            type="number"
                                        />
                                    </FormControl>
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="neutral"
                                        onClick={handleSendTest}
                                        disabled={sendingTest || !selectedTemplate || !testLeadId.trim() || !allVariablesMapped}
                                        loading={sendingTest}
                                    >
                                        Send Test
                                    </Button>
                                </Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                    Test sends count as a real template send for the duplicate-prevention rule.
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="solid"
                                    color="success"
                                    size="lg"
                                    startDecorator={sending ? <CircularProgress size="sm" /> : <SendIcon />}
                                    onClick={handleSend}
                                    disabled={sending || !selectedTemplate || !allVariablesMapped || previewCount === 0}
                                >
                                    {sending ? 'Sending...' : `Send to ${previewCount?.toLocaleString() || 0} Recipients`}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography level="title-md">Past WhatsApp Campaigns</Typography>
                                <IconButton size="sm" variant="plain" onClick={loadCampaigns} disabled={loadingCampaigns}>
                                    <RefreshIcon />
                                </IconButton>
                            </Box>

                            {loadingCampaigns ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : campaigns.length === 0 ? (
                                <Alert color="neutral" size="sm">
                                    No WhatsApp campaigns sent yet.
                                </Alert>
                            ) : (
                                <Sheet sx={{ overflow: 'auto', borderRadius: 'sm' }}>
                                    <Table size="sm" stickyHeader>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 140 }}>Date</th>
                                                <th>Template</th>
                                                <th style={{ width: 70, textAlign: 'center' }}>Sent</th>
                                                <th style={{ width: 70, textAlign: 'center' }}>Failed</th>
                                                <th style={{ width: 80, textAlign: 'center' }}>Skipped</th>
                                                <th style={{ width: 100, textAlign: 'center' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign) => (
                                                <tr key={campaign.id}>
                                                    <td>
                                                        <Typography level="body-xs">{formatDate(campaign.created_at)}</Typography>
                                                    </td>
                                                    <td>
                                                        <Tooltip title={campaign.template_body_preview || campaign.template_sid} size="sm" sx={{ maxWidth: 400 }}>
                                                            <Typography
                                                                level="body-xs"
                                                                sx={{
                                                                    maxWidth: 300,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {campaign.template_name || campaign.template_sid}
                                                            </Typography>
                                                        </Tooltip>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Typography level="body-xs" color="success">{campaign.sent_count}</Typography>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Typography level="body-xs" color={campaign.failed_count > 0 ? 'danger' : 'neutral'}>
                                                            {campaign.failed_count}
                                                        </Typography>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Typography level="body-xs" color="neutral">{campaign.skipped_count}</Typography>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <StatusChip status={campaign.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        Confirm WhatsApp Send
                    </Typography>
                    <Typography level="body-md" sx={{ mb: 2 }}>
                        You are about to send <strong>{selectedTemplate?.friendly_name || selectedTemplate?.sid}</strong> to{' '}
                        <strong>{previewCount?.toLocaleString()}</strong> recipients.
                    </Typography>
                    {skippedAlreadySent > 0 && (
                        <Typography level="body-sm" sx={{ mb: 2 }}>
                            {skippedAlreadySent.toLocaleString()} customers are excluded because they already received this template.
                        </Typography>
                    )}
                    <Alert color="warning" size="sm" sx={{ mb: 2 }}>
                        This action cannot be undone. The template/customer pair will be permanently marked as sent.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="solid" color="success" onClick={confirmSend}>
                            Send Campaign
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
